// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import { getWishListLinesDataView } from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import loadWishListIfNeeded from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListIfNeeded";
import loadWishListLines from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListLines";
import setAllWishListLinesIsSelected from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetAllWishListLinesIsSelected";
import setEditingSortOrder from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetEditingSortOrder";
import updateLoadWishListLinesParameter from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateLoadWishListLinesParameter";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LoadingOverlay } from "@insite/mobius/index";
import TextField from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useBuyingList from "@store/Framework/Common/Hooks/useBuyingList/useBuyingList";
import { makeHandlerChainAwaitable } from "@toolkit/Modules/client-framework/HandlerCreator";

import { CustomWidgetModule } from "@store/Framework/Types/types";
import BunzlToggle from "@store/Library/Components/Bunzl/Common/BunzlToggle";
import ManageProductsModal from "@store/Library/Components/BuyingList/ManageProductsModal";
import ShipToSelector from "@store/Library/Components/BuyingList/ShipToSelector";
import FadeIn from "@store/Library/Components/Reporting/Common/FadeIn";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";

import { myListsDetailsOptionsStyles } from "@store/Overrides/Widgets/MyListsDetails/MyListsDetailsOptions";
import { useDebounce } from "@store/Framework/Common/Hooks/useDebounce";
import { getMediaQuery } from "@toolkit/Framework/Common/Utilities/mediaQueries";
import debounce from "lodash/debounce";
import React, { ChangeEvent, useCallback, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mobileQuery = getMediaQuery({ option: "only", first: true, min: 0, max: 600, last: false });

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    wishListDataView: getWishListState(state, state.pages.myListDetails.wishListId),
    wishListLinesDataView: getWishListLinesDataView(state, state.pages.myListDetails.loadWishListLinesParameter),
    loadWishListLinesParameter: state.pages.myListDetails.loadWishListLinesParameter,
    selectedWishListLineIds: state.pages.myListDetails.selectedWishListLineIds,
    editingSortOrder: state.pages.myListDetails.editingSortOrder,
});

const mapDispatchToProps = {
    updateLoadWishListLinesParameter,
    loadWishListIfNeeded,
    loadWishListLines,
    setAllWishListLinesIsSelected,
    addToWishList: makeHandlerChainAwaitable<AddToWishListParameter, WishListModel>(addToWishList),
    setEditingSortOrder,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = myListsDetailsOptionsStyles;
interface BuyingListOptionsExtendedStyles {
    searchProductsGridItem: GridItemProps;
    infosBoxWrapper: InjectableCss;
    infosBox: InfosBoxProps;
}

const buyingListOptionsExtendedStyles: BuyingListOptionsExtendedStyles = {
    searchProductsGridItem: {
        css: css`
            width: 50%;
            display: flex;
            column-gap: 2rem;
            align-items: flex-end;

            ${mobileQuery} {
                width: 100%;
                order: 5;
                /* !important needed to override the styling created by the grid component */
                margin-top: 0.5rem !important;
            }
        `,
        width: [12, 12, 6, 6, 6],
    },
    infosBoxWrapper: {
        css: css`
            width: 100%;
            display: Flex;
            flex-direction: column;
            row-gap: 1rem;
            margin-bottom: 1rem;
        `,
    },
    infosBox: {
        extendedStyles: {
            wrapper: {
                css: css`
                    transition: all 0.3s ease-in-out;
                    margin-bottom: 0;
                `,
            },
        },
        size: "small" as const,
    },
};
const BuyingListOptions = ({
    wishListLinesDataView,
    wishListDataView,
    updateLoadWishListLinesParameter,
    loadWishListLines,
}: Props) => {
    const [query, setQuery] = useState("");
    const [addItemsToListIsOpen, setAddItemsToListIsOpen] = useState(false);
    const { activeBuyingList, toggleActiveBuyingListOverride, loading, activeWishList } = useBuyingList();

    const isAdmin = useAppSelector(state => getCurrentAccountState(state)?.value?.role === "Administrator");

    const debouncedSearch = useDebounce(
        useCallback(
            (query: string) => {
                if (!wishListDataView.value) {
                    return;
                }

                updateLoadWishListLinesParameter({ query });
                loadWishListLines();
            },
            [wishListDataView],
        ),
        400,
    );
    if (!activeBuyingList) {
        return null;
    }

    if (!activeWishList) {
        return null;
    }

    const searchChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const newQuery = event.target.value || "";
        setQuery(newQuery);
        debouncedSearch(newQuery);
    };

    const isBillToOverriden = activeBuyingList?.overrideBillToLists || false;
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="buyingListOptions_wrapper">
            <StyledWrapper {...styles.leftColumnWrapper} data-test-selector="buyingListOptions_leftColumn">
                <GridContainer {...styles.container} data-test-selector="buyingListOptions_container">
                    <GridItem
                        {...buyingListOptionsExtendedStyles.searchProductsGridItem}
                        data-test-selector="buyingListOptions_searchSection"
                    >
                        <TextField
                            disabled={wishListLinesDataView.value?.length === 0 && !query}
                            {...styles.search}
                            placeholder={translate("Search products in list")}
                            value={query || ""}
                            onChange={searchChangeHandler}
                            data-test-selector="buyingListOptions_searchField"
                        />
                    </GridItem>
                    <GridItem width={[12, 12, 6, 6, 6]} data-test-selector="buyingListOptions_adminSection">
                        {isAdmin && (
                            <LoadingOverlay
                                loading={loading}
                                data-test-selector="buyingListOptions_adminLoadingOverlay"
                            >
                                {activeBuyingList && activeBuyingList?.customers?.length > 0 && (
                                    <FadeIn appear animate>
                                        <StyledWrapper
                                            {...buyingListOptionsExtendedStyles.infosBoxWrapper}
                                            data-test-selector="buyingListOptions_infosBoxWrapper"
                                        >
                                            <InfosBox
                                                {...buyingListOptionsExtendedStyles.infosBox}
                                                type={isBillToOverriden ? "warning" : ("info" as any)}
                                                data-test-selector="buyingListOptions_billToInfoBox"
                                            >
                                                {isBillToOverriden
                                                    ? siteMessage("BuyingList_DoNotOverrideToBillTo")
                                                    : siteMessage("BuyingList_OverrideToBillTo")}
                                            </InfosBox>
                                            <BunzlToggle
                                                isToggled={isBillToOverriden}
                                                label={
                                                    isBillToOverriden
                                                        ? translate("Override Bill To Lists")
                                                        : translate("Do Not Override Bill To Lists")
                                                }
                                                setIsToggled={toggleActiveBuyingListOverride}
                                                data-test-selector="buyingListOptions_billToToggle"
                                            />
                                        </StyledWrapper>
                                    </FadeIn>
                                )}

                                <ShipToSelector
                                    buyingListId={activeWishList.properties?.buyingListId}
                                    data-test-selector="buyingListOptions_shipToSelector"
                                />
                            </LoadingOverlay>
                        )}
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
            <ManageProductsModal
                buyingList={activeWishList}
                isOpen={addItemsToListIsOpen}
                onClose={() => setAddItemsToListIsOpen(false)}
                data-test-selector="buyingListOptions_manageProductsModal"
            />
        </StyledWrapper>
    );
};

const widgetModule: CustomWidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BuyingListOptions),
    definition: {
        group: "Buying List",
        displayName: "Buying List Options",
    },
};

export default widgetModule;
